import { FileArchive, UploadCloud, X } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useSubmitNewVersion } from "@/hooks/use-skill-mutations";
import { cn } from "@/lib/utils";

const MAX_BYTES = 5 * 1024 * 1024;

function validateFile(file: File): string | null {
  const lower = file.name.toLowerCase();
  if (!lower.endsWith(".zip") && !lower.endsWith(".skill"))
    return "Only .zip or .skill files are allowed";
  if (file.size > MAX_BYTES) return "File exceeds the 5 MB limit";
  return null;
}

export function SubmitVersionDialog({
  slug,
  currentVersion,
  open,
  onOpenChange,
}: {
  slug: string;
  currentVersion: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [version, setVersion] = useState("");
  const [notes, setNotes] = useState("");
  const [over, setOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const submit = useSubmitNewVersion(slug);

  function reset() {
    setFile(null);
    setVersion("");
    setNotes("");
  }

  function accept(f: File | undefined) {
    if (!f) return;
    const err = validateFile(f);
    if (err) {
      toast.error(err);
      return;
    }
    setFile(f);
  }

  function handleSubmit() {
    if (!file) {
      toast.error("Attach a skill package");
      return;
    }
    if (!version.trim()) {
      toast.error("Version is required");
      return;
    }
    submit.mutate(
      { file, version: version.trim(), notes: notes.trim() || undefined },
      {
        onSuccess: () => {
          reset();
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Submit new version</DialogTitle>
          <DialogDescription>
            Upload an updated package and bump the version. The current version is v{currentVersion}
            ; previous versions stay in the history.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div>
            <div className="text-sm font-semibold text-fg-1">
              Skill package <span className="text-danger">*</span>
            </div>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setOver(true);
              }}
              onDragLeave={() => setOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setOver(false);
                accept(e.dataTransfer.files[0]);
              }}
              className={cn(
                "mt-2 flex w-full flex-col items-center gap-2 rounded-[12px] border-2 border-dashed px-6 py-8 text-center transition-colors",
                over ? "border-fg-link bg-bg-2" : "border-border-2 bg-white hover:bg-bg-2",
              )}
            >
              <UploadCloud className="size-7 text-fg-link" />
              <span className="text-sm text-fg-3">Click to browse · .zip or .skill, max 5 MB</span>
            </button>
            <input
              ref={inputRef}
              type="file"
              accept=".zip,.skill"
              className="hidden"
              onChange={(e) => accept(e.target.files?.[0])}
            />
          </div>

          {file ? (
            <div className="flex items-center gap-3 rounded-[9px] border border-border-1 bg-white p-3">
              <span className="grid size-9 place-items-center rounded-md bg-bg-2 text-fg-link">
                <FileArchive className="size-4" />
              </span>
              <div className="flex-1">
                <div className="text-sm font-medium text-fg-1">{file.name}</div>
                <div className="text-xs text-fg-3">{(file.size / 1024).toFixed(1)} KB</div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFile(null)}
                aria-label="Remove file"
              >
                <X className="size-4" />
              </Button>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="new-version">Version</Label>
            <Input
              id="new-version"
              className="font-mono"
              placeholder="e.g. 1.1.0"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-version-notes">
              Release notes <span className="font-normal text-fg-3">(optional)</span>
            </Label>
            <Textarea
              id="new-version-notes"
              className="min-h-[80px]"
              placeholder="What changed in this version?"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submit.isPending}>
            {submit.isPending ? "Publishing…" : "Publish version"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
